import { z } from "zod";

/**
 * Monster Truck Arena protocol (ADR-0016, races with AI trucks ADR-0032). Free to drive: no chips, no prizes; lap times
 * last for the visit, and people's best laps are kept for the daily and all-time boards.
 *
 * Getting in and out uses the street cars' `vehicle.action` / `vehicle.result` (same codes) and driving uses
 * `input.drive` (same sequence and budget rules); the arena's game answers them. `venue.action` carries the race
 * controls only. The shared state (`venue.state`, at most 15 Hz while a truck moves, and on every change) carries every
 * truck, everyone's lap times and the race; the fastest laps boards come in each visitor's `venue.private` (sent when
 * they walk in and when a board changes), so the frequent shared state stays small. Truck ids, numbers, paints and bays
 * are static (@townsy/world TRUCK_FLEET).
 */
export const TrucksAction = z.discriminatedUnion("type", [
  /**
   * Race (drivers only): with no race on (or its results showing) this opens a 10 s lobby; during the lobby it joins
   * it. Either way the server puts the driver's truck on a free place on the starting grid ("Start a race", "Join the
   * race", "Race again"). Driving onto the grid during the lobby joins too.
   */
  z.object({ type: z.literal("race.start") }).strict(),
]);
export type TrucksAction = z.infer<typeof TrucksAction>;

/**
 * The Monster Truck Arena's AI racers (ADR-0032): original names, ONE list shared by the realtime AI racers and the
 * display-name check. The founder reserved these names (2026-10-21): no visitor or account may take one as a display
 * name (case, accents, leetspeak digits, separators and repeated letters ignored, as the name filter normalizes); see
 * `isAiRacerName` in the API. Accounts that already had one keep it; only new picks are refused. The AI residents'
 * first names are reserved separately (AI_RESIDENT_NAMES); these are the racers only.
 */
export const AI_TRUCK_RACERS = [
  { key: "dusty", name: "Dusty" },
  { key: "rumble", name: "Rumble" },
  { key: "gravelgus", name: "Gravel Gus" },
  { key: "turbotess", name: "Turbo Tess" },
  { key: "mudslidemo", name: "Mudslide Mo" },
  { key: "bigbertha", name: "Big Bertha" },
  { key: "rocketrae", name: "Rocket Rae" },
  { key: "dieseldot", name: "Diesel Dot" },
  { key: "sprocket", name: "Sprocket" },
  { key: "boulder", name: "Boulder" },
] as const satisfies readonly { key: string; name: string }[];

/** The plain refusal for a display name that is one of the AI racers' names. */
export const AI_RACER_NAME_REFUSED_REASON = "That name belongs to one of Townsy's AI racers. Please choose a different name.";

export type TruckMode = "parked" | "driven" | "returning";

/** One truck in `venue.state`. */
export interface TruckView {
  id: string;
  mode: TruckMode;
  x: number;
  z: number;
  /** Height of the wheels' contact plane above the floor (0 on the flat dirt). */
  y: number;
  /** Vertical speed (m/s). */
  vy: number;
  heading: number;
  /** m/s along the heading (negative reversing). */
  speed: number;
  airborne: boolean;
  /** Public actor id of the driver ("" when nobody drives it). */
  driverActorId: string;
  /** Last `input.drive` sequence applied for the current driver (prediction acknowledges with it). */
  lastSeq: number;
  /**
   * Simulation steps this truck has taken in this room (one per applied input, per AI or coasting step, and one per
   * teleport). Other browsers play a truck back on this even 50 ms timeline (ADR-0032), so a driver whose inputs reach
   * the server unevenly is still drawn moving evenly.
   */
  steps: number;
  /** Race countdown: the truck is held on the grid. */
  held: boolean;
}

/** One visitor's lap times this visit (shared: anyone can see the board; nothing is kept after leaving). */
export interface TruckDriverView {
  actorId: string;
  label: string;
  /** The truck they drive now (null on foot). */
  truckId: string | null;
  /** Index of the next checkpoint to cross (0 = the start/finish line). */
  nextCheckpoint: number;
  /** Server time (ms) the current timed lap began; null before crossing the start line. */
  lapStartedAt: number | null;
  laps: number;
  lastLapMs: number | null;
  bestLapMs: number | null;
}

export type TruckRacePhase = "idle" | "lobby" | "countdown" | "racing" | "results";

export interface TruckRacerView {
  /** The visitor's public actor id, or an AI racer's (`act_00ai...`, see `ai`). */
  actorId: string;
  label: string;
  /** An AI racer (server-driven; shown with the AI badge; never in the fastest laps). */
  ai: boolean;
  truckId: string;
  /** Starting grid slot (0-based). */
  slot: number;
  /** Laps completed in this race. */
  laps: number;
  /** Next checkpoint (0 = the line). */
  nextCheckpoint: number;
  /** Best lap of this race (ms), null before the first full lap. */
  bestLapMs: number | null;
  /** Race time when they finished (ms), null while racing. */
  finishMs: number | null;
  /** Did not finish: got out, left, or not over the line within the time after the winner. */
  dnf: boolean;
  /** 1-based position: finishers by time, then everyone still racing by how far round they are, then DNF. */
  position: number;
}

export interface TruckRaceView {
  phase: TruckRacePhase;
  /** Server time (ms) the current phase ends (lobby, countdown, results; racing: the time limit). */
  endsAt: number | null;
  /** Server time of the green light (racing and results). */
  startedAt: number | null;
  laps: number;
  /** Trucks a race is filled up to with AI racers (0: AI racers are off). */
  field: number;
  /** Who opened the race (display label). */
  openedBy: string | null;
  /** Lobby: people on the grid now. From the countdown on: the racers (people and AI). */
  racers: TruckRacerView[];
}

/** One line of a fastest laps board: a person's best lap (AI racers, test and staff accounts are never on it). */
export interface TruckFastestLap {
  actorId: string;
  /** The driver's current display name. */
  label: string;
  lapMs: number;
}

/** The arena's two boards (ADR-0032), kept in the database, best first, one line per driver. */
export interface TruckLapBoards {
  /** The UTC day "today" is (YYYY-MM-DD). */
  day: string;
  /** Fastest laps today (UTC). */
  today: TruckFastestLap[];
  /** All-time fastest laps (each driver's best day). */
  allTime: TruckFastestLap[];
}

export interface TrucksState {
  /** Server clock (ms) when this state was sent: the client derives its clock offset for timers. */
  now: number;
  trucks: TruckView[];
  drivers: TruckDriverView[];
  race: TruckRaceView;
}

export interface TrucksPrivate {
  /** The truck this visitor drives (null on foot). */
  truckId: string | null;
  /** The fastest laps boards (at most TRUCK_RACE.boardSize lines each). */
  boards: TruckLapBoards;
}

/**
 * Race rules (ADR-0016, ADR-0032): a 10 s lobby, a 3-2-1 countdown, 3 laps (the first crossing of the line after the
 * green light starts lap 1; three more crossings, each after every checkpoint in order, finish), then the results.
 * AI racers fill the grid up to `field` trucks. The HUD, the server rule and the docs all read `laps` from here.
 */
export const TRUCK_RACE = {
  lobbyMs: 10_000,
  countdownMs: 3_000,
  laps: 3,
  /** Trucks on the grid with the AI racers (people are never turned away: up to the six grid places). */
  field: 5,
  /** After the winner crosses the line the others have this long; then they are shown as DNF. */
  finishWindowMs: 45_000,
  /** A race never runs longer than this. */
  maxRaceMs: 6 * 60_000,
  resultsMs: 15_000,
  /** An AI racer that finished drives on this long, then is towed back to its bay. */
  aiCooldownMs: 2_500,
  /** Lines on each fastest laps board (today, all time). */
  boardSize: 10,
} as const;
